// The link the hero has attached, and the chip it renders once a preview resolves.

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import type { PreviewSuccess } from './errorCodes';
import { linkFallbackTitle } from './linkFallbackTitle';
import type { SocialPostPlatformId } from './platforms';
import { platformDomain } from './platforms';
import type { SocialPostClassification } from './utils';

export type AttachedPost = { platform: SocialPostPlatformId; url: string; handle: string | undefined };

export type Chip = {
	platform: SocialPostPlatformId;
	/** The source's own name when the server knows it, otherwise the platform's domain. */
	sourceLabel: string;
	/** The source's logo or favicon, when the server has one. */
	sourceLogo: string | undefined;
	handle: string | undefined;
	/** The page's title, or the link's host and path when the source gave none, so the chip always names the link. */
	title: string;
	description: string | undefined;
	image: string | undefined;
};

/**
 * Properties for homepage_social_post_link_added: the link as the rest of the funnel tracks it
 * (normalized for a supported link, as entered otherwise) and whether it was accepted.
 */
export function linkAddedProperties(
	link: Exclude<SocialPostClassification, { kind: 'freeform' }>,
	text: string,
	inputMethod: TrackingProperties['input_method'],
): TrackingProperties {
	if (link.kind === 'supported_link') {
		return { source: link.url, platform: link.platform, link_status: 'supported', input_method: inputMethod };
	}
	return { source: text, link_status: link.kind, input_method: inputMethod };
}

export function toAttachedPost(link: Extract<SocialPostClassification, { kind: 'supported_link' }>): AttachedPost {
	return { platform: link.platform, url: link.url, handle: link.handle };
}

export function toChip(post: AttachedPost | undefined, preview: PreviewSuccess | undefined): Chip | undefined {
	if (!post || !preview) return undefined;
	return {
		platform: post.platform,
		sourceLabel: preview.sourceLabel ?? platformDomain(post.platform),
		sourceLogo: preview.sourceLogo ?? undefined,
		handle: post.handle,
		title: preview.title ?? linkFallbackTitle(post.url),
		description: preview.description ?? undefined,
		image: preview.image ?? undefined,
	};
}
